import { PaginatedResponse } from '@eagle/api-types';
import { Dashboard, DashboardType } from '@eagle/core-data-types';
import { ListItemButton, Popover, Stack, styled, SvgIcon, Typography } from '@ethings/ui-toolkit';
import { Box, Link, SxProps, useMediaQuery, useTheme } from '@mui/material';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { useCustomRoutes, usePromise } from '../../hooks';
import { scrollbar } from '../../style';
import { Nullable } from '../../types';
import { testid } from '../../util';
import { MiddleSpinner } from '../middle-spinner';

export interface DashboardSwitcherStaticItem {
  id: string;
  label: string;
  path: string;
}

interface DashboardSwitcherProps {
  children: ReactNode;
  dashboardId?: string;
  staticItems?: DashboardSwitcherStaticItem[];
  type: DashboardType;
  sx?: SxProps;
}

const StyledPopover = styled(Popover)(({ theme }) => ({
  zIndex: 1400,
  marginTop: theme.spacing(1),
  maxHeight: '550px',
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  height: theme.spacing(1.5),
  marginLeft: theme.spacing(0.5),
  width: theme.spacing(1.5),
}));

const DashboardListItemButton = styled(ListItemButton)({
  paddingLeft: '16px',
  paddingRight: '16px',
});

const DashboardListItemButtonWithState = styled(ListItemButton)<{ isCurrent: boolean }>(({ isCurrent }) => ({
  paddingLeft: '16px',
  paddingRight: '16px',
  ...(isCurrent && {
    pointerEvents: 'none',
    opacity: 0.6,
  }),
}));

export const DashboardSwitcher: FC<DashboardSwitcherProps> = ({ children, dashboardId, staticItems, type, sx = {} }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const open = Boolean(anchorEl);

  const clickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const onClose = (): void => {
    setAnchorEl(null);
  };

  return <>
    <Link
      data-testid="dropdown-button"
      color='inherit'
      component='button'
      onClick={clickHandler}
      sx={{ lineHeight: 'inherit', textAlign: 'left', ...sx }}
      underline='none'
      variant='caption'
    >
      <Stack alignItems="center" direction="row">
        <Box sx={{ overflow: 'hidden' }}>{children}</Box>
        <StyledSvgIcon><path d="m1 8 10 10 10-10z"></path></StyledSvgIcon>
      </Stack>
    </Link>
    <StyledPopover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      open={open}
      onClose={onClose}
      onClick={(e) => { e.stopPropagation(); }}
    >
      <DashboardSwitcherInner
        dashboardId={dashboardId}
        onSelect={onClose}
        staticItems={staticItems}
        type={type}
      />
    </StyledPopover>
  </>;
};

interface DashboardSwitcherInnerProps {
  dashboardId?: string;
  onSelect: (value: string) => void;
  staticItems?: DashboardSwitcherStaticItem[];
  type: DashboardType;
}

interface RequestParams {
  filter?: Record<string, any>;
  limit?: number;
  search?: string;
  sort: Record<string, string>;
}

const DashboardSwitcherInner: FC<DashboardSwitcherInnerProps> = ({ dashboardId, onSelect, staticItems = [], type }) => {
  const { t } = useTranslation(['common']);
  const { axios } = useAuthenticated();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const searchRef = useRef<HTMLInputElement>(undefined);
  const customRoutes = useCustomRoutes();

  const [dashboards, , dashboardStatus] = usePromise<Nullable<Dashboard[]>>(async () => {
    const params: RequestParams = {
      sort: { display: 'asc' },
      filter: { type, ...(dashboardId ? { _id: { $ne: dashboardId } } : {}) },
    };

    const response = await axios.get<PaginatedResponse<Dashboard>>('/api/v2/my/dashboards', {
      params,
    });

    return response.data.items;
  }, [type, axios, dashboardId]);

  useEffect(() => {
    if (!isSmallScreen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isSmallScreen]);

  const isLoading = dashboardStatus !== 'resolved';
  const isReport = type === DashboardType.REPORT;

  return <>
    <Stack width={250} maxWidth="100%">
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption">{t('common:component.navigation.switch-to.labels')}</Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', ...scrollbar }}>
        {!isLoading
          ? <div>
            {staticItems.map((item) => (
              <DashboardListItemButton
                data-testid={testid`dashboard-switcher-item-${item.id}`}
                key={item.id}
                onClick={() => {
                  onSelect(item.id);
                  navigate(item.path);
                }}
              >
                {item.label}
              </DashboardListItemButton>
            ))}
            {dashboards?.length === 0 && staticItems.length === 0
              && <Typography data-testid="dashboard-switcher-no-result" variant="body2" px={2} pt={0} pb={1}>
                {t('common.hint.list.no-results')}
              </Typography>
            }
            {dashboards?.map((dashboard) => (
              <DashboardListItemButton
                data-testid={testid`dashboard-switcher-item-${dashboard._id}`}
                key={dashboard._id}
                onClick={() => {
                  onSelect(dashboard._id);
                  navigate(`/${isReport ? customRoutes.reports : customRoutes.dashboards}/${dashboard._id}`);
                }}
              >
                {dashboard.display}
              </DashboardListItemButton>
            ))}
          </div>
          : <MiddleSpinner sx={{ py: 2 }} size={20} />
        }
      </Box>
    </Stack>
  </>;
};

interface DashboardSwitcherLocalProps {
  children: ReactNode;
  dashboardId?: string;
  staticItems?: DashboardSwitcherStaticItem[];
  type: DashboardType;
}

export const DashboardSwitcherLocal: FC<DashboardSwitcherLocalProps> = ({ children, dashboardId, staticItems, type }) => {
  const [anchor, setAnchor] = useState<Nullable<HTMLElement>>(null);
  const open = Boolean(anchor);

  const clickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  };

  const onClose = (): void => {
    setAnchor(null);
  };

  return <>
    <Link
      data-testid="dropdown-button"
      color='inherit'
      component='button'
      onClick={clickHandler}
      sx={{ lineHeight: 'inherit', textAlign: 'left' }}
      underline='none'
      variant='caption'
    >
      <Stack alignItems="center" direction="row">
        <Box sx={{ overflow: 'hidden' }}>{children}</Box>
        <StyledSvgIcon><path d="m1 8 10 10 10-10z"></path></StyledSvgIcon>
      </Stack>
    </Link>
    <StyledPopover
      anchorEl={anchor}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      open={open}
      onClose={onClose}
      onClick={(e) => { e.stopPropagation(); }}
    >
      <DashboardSwitcherLocalInner
        type={type}
        dashboardId={dashboardId}
        onSelect={onClose}
        staticItems={staticItems}
      />
    </StyledPopover>
  </>;
};

interface DashboardSwitcherLocalInnerProps {
  dashboardId?: string;
  onSelect: (value: string) => void;
  staticItems?: DashboardSwitcherStaticItem[];
  type: DashboardType;
}

const DashboardSwitcherLocalInner: FC<DashboardSwitcherLocalInnerProps> = ({ dashboardId, onSelect, staticItems = [], type }) => {
  const { t } = useTranslation(['common']);
  const { axios } = useAuthenticated();
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const searchRef = useRef<HTMLInputElement>(undefined);

  const getSelectedType = (type: string): string | undefined => {
    if (type.includes('report')) {
      return 'reports';
    }
    if (type.includes('dashboard')) {
      return 'dashboards';
    }
    if (type.includes('analytic')) {
      return 'analytics';
    }
    return undefined;
  };

  const [dashboards, , dashboardStatus] = usePromise<Nullable<Dashboard[]>>(async () => {
    const params: RequestParams = {
      sort: { display: 'asc' },
      filter: { type },
      limit: 500,
    };

    const response = await axios.get<PaginatedResponse<Dashboard>>('/api/v2/my/dashboards', {
      params,
    });
    return response.data.items;
  }, [axios, type]);

  useEffect(() => {
    if (!isSmallScreen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isSmallScreen]);

  const isLoading = dashboardStatus !== 'resolved';

  return <>
    <Stack width={250} maxWidth="100%">
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption">{t('common:component.navigation.switch-to.labels')}</Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', ...scrollbar }}>
        {!isLoading
          ? <div>
            {staticItems.map((item) => (
              <DashboardListItemButton
                data-testid={testid`dashboard-switcher-item-${item.id}`}
                key={item.id}
                onClick={() => {
                  onSelect(item.id);
                  navigate(item.path);
                }}
              >
                {item.label}
              </DashboardListItemButton>
            ))}
            {dashboards?.length === 0 && staticItems.length === 0
              && <Typography data-testid="dashboard-switcher-no-result" variant="body2" px={2} pt={0} pb={1}>
                {t('common.hint.list.no-results')}
              </Typography>
            }
            {dashboards?.map((dashboard) => (
              <DashboardListItemButtonWithState
                data-testid={testid`dashboard-switcher-item-${dashboard._id}`}
                key={dashboard._id}
                onClick={() => {
                  onSelect(dashboard._id);
                  const dashboardType = getSelectedType(dashboard.type);
                  navigate(`/${dashboardType ?? 'unknown'}/${dashboard._id}`);
                }}
                isCurrent={dashboard._id === dashboardId}
              >
                {dashboard.display}
              </DashboardListItemButtonWithState>
            ))}
          </div>
          : <MiddleSpinner sx={{ py: 2 }} size={20} />
        }
      </Box>
    </Stack>
  </>;
};
